<template>
    <transition
        enter-active-class="collapsing"
        leave-active-class="collapsing"
        @enter="onEnter"
        @afterEnter="onAfterEnter"
        @leave="onLeave"
        @afterLeave="onAfterLeave"
    >
        <dynamic-element
            v-show="show"
            :id="id || null"
            :tag="tag"
            class="collapse"
            :data-accordion="accordion"
        >
            <slot />
        </dynamic-element>
    </transition>
</template>

<script>
import listenOnRootMixin from '@/mixins/listen-on-root';
import { reflow } from '@/utils/dom';
import DynamicElement from './dynamic-element';

const EVENT_ACCORDION = 'flex::collapse::accordion';

export default {
    components: { DynamicElement },
    mixins: [listenOnRootMixin],

    model: {
        prop: 'visible',
        event: 'input',
    },

    props: {
        id: {
            type: String,
            required: true,
        },

        accordion: {
            type: String,
            default: '',
        },

        autoOpen: {
            type: Boolean,
            default: false,
        },

        tag: {
            type: String,
            default: 'div',
        },

        visible: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            show: this.visible,
            stopEmit: false,
        };
    },

    watch: {
        visible(newVal) {
            if (newVal !== this.show) {
                this.show = newVal;
            }
        },

        show(newVal, oldVal) {
            if (newVal !== oldVal) {
                this.emitState();
            }
        },
    },

    created() {
        this.listenOnRoot(EVENT_ACCORDION, this.handleAccordionEvt);
    },

    mounted() {
        this.emitState();

        const group = document.querySelectorAll(`[data-accordion="${ this.accordion }"]`);

        // auto-open the collapse component if it only has one child element
        if (this.autoOpen && this.accordion && group.length === 1) {
            this.stopEmit = true;
            this.show = true;
        }
    },

    methods: {
        toggle() {
            this.show = !this.show;
        },

        onEnter(el) {
            el.style.height = 0;
            reflow(el);
            el.style.height = `${ el.scrollHeight }px`;
            this.$emit('show');
        },

        onAfterEnter(el) {
            el.style.height = null;
            this.$emit('shown');
        },

        onLeave(el) {
            el.style.height = 'auto';
            el.style.display = 'block';
            el.style.height = `${ el.getBoundingClientRect().height }px`;
            reflow(el);
            el.style.height = 0;
            this.$emit('hide');
        },

        onAfterLeave(el) {
            el.style.height = null;
            this.$emit('hidden');
        },

        emitState() {
            if (this.stopEmit) {
                this.stopEmit = false;
                return;
            }

            this.$emit('input', this.show);

            if (this.accordion && this.show) {
                this.$root.$emit(EVENT_ACCORDION, this.id, this.accordion);
            }
        },

        handleAccordionEvt(openedId, accordion) {
            if (!this.accordion || accordion !== this.accordion) return;

            if (openedId === this.id) {
                // Open this collapse if not shown
                if (!this.show) {
                    this.toggle();
                }
            } else if (this.show) {
                // Close this collapse if shown
                this.toggle();
            }
        },
    },
};
</script>

<style lang="scss">
.collapse {
    transition: height 0.5s cubic-bezier(.25, .46, .45, .94);

    &.collapsing {
        overflow: hidden;
    }
}
</style>
